// ----------------------------------------------------------------------------
// BucketService — canonical shared implementation (multi-bucket).
// Used by both the Electron xtrc app and the azathoth legacy web apps.
// ----------------------------------------------------------------------------
// Consumers must have `client-zip` available in their dependency tree.
// IDB schema v2: `items` store gains `bucketId` field; `buckets` store added.
// ----------------------------------------------------------------------------

import { downloadZip } from 'client-zip';
import { Midi } from '@tonejs/midi';
const { drawWaveformFromInt16, drawWaveformFromInt32, getThemeDrawColors, playAudioBuffer, getGlobalAudioContext } = require('../../common/draw-utils.cjs');
const { drawTrackerGrid: drawTrackerGridShared } = require('../../common/module-cards.cjs');
const { openVideoPlayer, drawFirstFrame } = require('../video-player.cjs');

const DB_NAME    = 'xtrc-bucket';
const DB_VERSION = 2;
const S_ITEMS    = 'items';
const S_BUCKETS  = 'buckets';
const DEFAULT_ID = 'default';

// ---- MIDI piano roll drawing helper -----------------------------------------
export function drawMidiPianoRoll(canvas, midiData) {
   const ctx = canvas.getContext('2d');
   const W = canvas.width, H = canvas.height;

   // Use theme colors
   const style = getComputedStyle(document.body);
   const bgColor = style.getPropertyValue('--c0').trim() || '#1a1a1a';
   const noteColor = style.getPropertyValue('--c9').trim() || '#c0c0c0';

   ctx.fillStyle = bgColor;
   ctx.fillRect(0, 0, W, H);

   try {
      const midi = new Midi(midiData);
      let allNotes = [];
      midi.tracks.forEach(track => {
         if (track.notes) {
            allNotes = allNotes.concat(track.notes);
         }
      });

      if (allNotes.length === 0) {
         // Empty MIDI - draw placeholder
         ctx.fillStyle = noteColor;
         ctx.fillRect(0, H/2 - 1, W, 2);
         return;
      }

      const minNote = Math.min(...allNotes.map(n => n.midi));
      const maxNote = Math.max(...allNotes.map(n => n.midi));
      const noteRange = maxNote - minNote + 1;
      const noteHeight = H / noteRange;
      const duration = Math.max(...allNotes.map(n => n.time + n.duration));
      const timeScale = W / duration;

      ctx.fillStyle = noteColor;
      for (const note of allNotes) {
         const x = note.time * timeScale;
         const y = H - ((note.midi - minNote + 1) * noteHeight);
         const w = note.duration * timeScale;
         const h = noteHeight - 1;
         ctx.fillRect(x, y, w, h);
      }
   } catch (e) {
      ctx.fillStyle = noteColor;
      ctx.fillRect(0, H/2 - 1, W, 2);
   }
}

// ---- Tracker grid drawing helper for module files --------------------------
// Delegates to the shared drawTrackerGrid from module-cards.cjs
// which uses theme colors and detects format from magic bytes
export function drawTrackerGrid(canvas, moduleData) {
   drawTrackerGridShared(canvas, moduleData, null, null, null, null);
}

// ---- Archive icon drawing helper for raw/archive files ---------------------
// Draws a simple archive/ISO icon for non-audio, non-midi, non-module items
export function drawArchiveIcon(canvas) {
   const ctx = canvas.getContext('2d');
   const W = canvas.width, H = canvas.height;

   const style = getComputedStyle(document.body);
   const bgColor = style.getPropertyValue('--c0').trim() || '#1a1a1a';
   const fgColor = style.getPropertyValue('--c9').trim() || '#c0c0c0';
   const midColor = style.getPropertyValue('--c6').trim() || '#666666';

   ctx.fillStyle = bgColor;
   ctx.fillRect(0, 0, W, H);

   // Draw a simple archive/box icon
   const cx = W / 2, cy = H / 2;
   const boxW = Math.min(W * 0.5, 30);
   const boxH = Math.min(H * 0.5, 25);

   // Box outline
   ctx.strokeStyle = fgColor;
   ctx.lineWidth = 1.5;
   ctx.strokeRect(cx - boxW / 2, cy - boxH / 2, boxW, boxH);

   // Internal lines (like archive contents)
   ctx.strokeStyle = midColor;
   ctx.lineWidth = 0.5;
   for (let i = 1; i < 4; i++) {
      const y = cy - boxH / 2 + (boxH / 4) * i;
      ctx.beginPath();
      ctx.moveTo(cx - boxW / 2 + 2, y);
      ctx.lineTo(cx + boxW / 2 - 2, y);
      ctx.stroke();
   }
}

// ---- Video icon drawing helper for video files ----------------------------
// Draws a simple video/film icon for video items
export function drawVideoIcon(canvas) {
   const ctx = canvas.getContext('2d');
   const W = canvas.width, H = canvas.height;

   const style = getComputedStyle(document.body);
   const bgColor = style.getPropertyValue('--c0').trim() || '#1a1a1a';
   const fgColor = style.getPropertyValue('--c9').trim() || '#c0c0c0';
   const midColor = style.getPropertyValue('--c6').trim() || '#666666';

   ctx.fillStyle = bgColor;
   ctx.fillRect(0, 0, W, H);

   // Draw a film/play icon
   const cx = W / 2, cy = H / 2;
   const boxW = Math.min(W * 0.45, 28);
   const boxH = Math.min(H * 0.5, 22);

   // Film strip outline
   ctx.strokeStyle = fgColor;
   ctx.lineWidth = 1.5;
   ctx.strokeRect(cx - boxW / 2, cy - boxH / 2, boxW, boxH);

   // Sprocket holes (top and bottom)
   ctx.fillStyle = midColor;
   const holeW = 3, holeH = 2;
   for (let i = 0; i < 4; i++) {
      const x = cx - boxW / 2 + 3 + i * (boxW - 6) / 3;
      ctx.fillRect(x, cy - boxH / 2 + 1, holeW, holeH);
      ctx.fillRect(x, cy + boxH / 2 - 3, holeW, holeH);
   }

   // Play triangle in center
   ctx.fillStyle = fgColor;
   const playSize = Math.min(boxW, boxH) * 0.3;
   ctx.beginPath();
   ctx.moveTo(cx - playSize / 3, cy - playSize / 2);
   ctx.lineTo(cx - playSize / 3, cy + playSize / 2);
   ctx.lineTo(cx + playSize / 2, cy);
   ctx.closePath();
   ctx.fill();
}

// ---- Encoding progress drawing helper for video items being converted -------
// Draws a video icon with a progress bar overlay (0..1)
export function drawEncodingProgress(canvas, progress, label) {
   const ctx = canvas.getContext('2d');
   const W = canvas.width, H = canvas.height;

   const style = getComputedStyle(document.body);
   const bgColor = style.getPropertyValue('--c0').trim() || '#1a1a1a';
   const fgColor = style.getPropertyValue('--c9').trim() || '#c0c0c0';
   const midColor = style.getPropertyValue('--c6').trim() || '#666666';
   const accentColor = style.getPropertyValue('--c5').trim() || fgColor;

   ctx.fillStyle = bgColor;
   ctx.fillRect(0, 0, W, H);

   // Draw a dimmed video icon in the background
   ctx.globalAlpha = 0.35;
   const cx = W / 2, cy = H / 2 - 4;
   const boxW = Math.min(W * 0.45, 28);
   const boxH = Math.min(H * 0.4, 18);
   ctx.strokeStyle = fgColor;
   ctx.lineWidth = 1.5;
   ctx.strokeRect(cx - boxW / 2, cy - boxH / 2, boxW, boxH);
   ctx.fillStyle = fgColor;
   const playSize = Math.min(boxW, boxH) * 0.3;
   ctx.beginPath();
   ctx.moveTo(cx - playSize / 3, cy - playSize / 2);
   ctx.lineTo(cx - playSize / 3, cy + playSize / 2);
   ctx.lineTo(cx + playSize / 2, cy);
   ctx.closePath();
   ctx.fill();
   ctx.globalAlpha = 1;

   // Progress bar at the bottom
   const barY = H - 14;
   const barH = 6;
   const barX = 8;
   const barW = W - 16;
   ctx.fillStyle = midColor;
   ctx.fillRect(barX, barY, barW, barH);

   const pct = Math.max(0, Math.min(1, progress));

   if (pct > 0) {
      // Determinate: fill bar proportionally
      ctx.fillStyle = accentColor;
      ctx.fillRect(barX, barY, barW * pct, barH);
   } else {
      // Indeterminate: animated sliding segment (when duration unknown / no progress yet)
      const segW = barW * 0.3;
      const phase = (Date.now() / 800) % 1;
      const segX = barX + phase * (barW - segW);
      ctx.fillStyle = accentColor;
      ctx.fillRect(segX, barY, segW, barH);
   }

   // Percentage text
   ctx.fillStyle = fgColor;
   ctx.font = 'bold 8px monospace';
   ctx.textBaseline = 'top';
   ctx.textAlign = 'center';
   const pctText = pct > 0 ? `${Math.round(pct * 100)}%` : '...';
   ctx.fillText(pctText, W / 2, barY - 10);

   // Label (e.g., "ENCODING")
   if (label) {
      ctx.font = 'bold 6px monospace';
      ctx.fillText(label, W / 2, 2);
   }
   ctx.textAlign = 'left';
}

// Draws a simple image icon for image items
export function drawImageIcon(canvas) {
   const ctx = canvas.getContext('2d');
   const W = canvas.width, H = canvas.height;

   const style = getComputedStyle(document.body);
   const bgColor = style.getPropertyValue('--c0').trim() || '#1a1a1a';
   const fgColor = style.getPropertyValue('--c9').trim() || '#c0c0c0';
   const midColor = style.getPropertyValue('--c6').trim() || '#666666';

   ctx.fillStyle = bgColor;
   ctx.fillRect(0, 0, W, H);

   // Draw a picture frame icon
   const cx = W / 2, cy = H / 2;
   const boxW = Math.min(W * 0.45, 28);
   const boxH = Math.min(H * 0.5, 22);

   ctx.strokeStyle = fgColor;
   ctx.lineWidth = 1.5;
   ctx.strokeRect(cx - boxW / 2, cy - boxH / 2, boxW, boxH);

   // Mountain + sun
   ctx.fillStyle = midColor;
   ctx.beginPath();
   ctx.moveTo(cx - boxW / 2 + 2, cy + boxH / 2 - 2);
   ctx.lineTo(cx - boxW / 4, cy);
   ctx.lineTo(cx, cy + boxH / 2 - 2);
   ctx.closePath();
   ctx.fill();
   ctx.beginPath();
   ctx.arc(cx + boxW / 4, cy - boxH / 4, 3, 0, Math.PI * 2);
   ctx.fill();
}

// Draws a small type badge in the top-left corner of a bucket item canvas.
// Used by render() so every card shows its file type (WAV, MIDI, MOD, VIDEO, IMAGE, RAW, ...).
export function drawTypeBadge(canvas, label) {
   const ctx = canvas.getContext('2d');
   const style = getComputedStyle(document.body);
   const fgColor = style.getPropertyValue('--c9').trim() || '#c0c0c0';
   ctx.fillStyle = fgColor;
   ctx.font = 'bold 7px monospace';
   ctx.textBaseline = 'top';
   ctx.fillText(label, 2, 2);
}

// Draw image thumbnail on canvas (async), returns true if successful
async function drawImageThumbnail(data, mimeType, canvas, label) {
   const ctx = canvas.getContext('2d');
   const W = canvas.width, H = canvas.height;

   const style = getComputedStyle(document.body);
   const bgColor = style.getPropertyValue('--c0').trim() || '#1a1a1a';
   const fgColor = style.getPropertyValue('--c9').trim() || '#c0c0c0';

   ctx.fillStyle = bgColor;
   ctx.fillRect(0, 0, W, H);

   try {
      const blob = new Blob([data], { type: mimeType || 'image/png' });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      await new Promise((resolve, reject) => {
         img.onload = resolve;
         img.onerror = reject;
         img.src = url;
         setTimeout(resolve, 3000); // timeout for unsupported formats
      });

      if (img.width > 0 && img.height > 0) {
         // Draw centered, scaled to fit (leaving room for label bar)
         const availH = H - 12;
         const scale = Math.min(W / img.width, availH / img.height);
         const drawW = img.width * scale;
         const drawH = img.height * scale;
         const drawX = (W - drawW) / 2;
         const drawY = (availH - drawH) / 2;
         ctx.drawImage(img, drawX, drawY, drawW, drawH);

         // Label bar at bottom
         ctx.fillStyle = bgColor;
         ctx.globalAlpha = 0.75;
         ctx.fillRect(0, H - 10, W, 10);
         ctx.globalAlpha = 1;
         ctx.fillStyle = fgColor;
         ctx.font = 'bold 7px monospace';
         const labelText = label ? label.substring(0, 28) : 'image';
         ctx.fillText(labelText, 2, H - 2);

         URL.revokeObjectURL(url);
         return true;
      }
      URL.revokeObjectURL(url);
   } catch (e) {
      // Fall through to icon
   }
   return false;
}

// ---- IDB open (singleton — one connection for the page lifetime) ------------
let _dbPromise = null;
let _persistRequested = false;

// Request persistent storage so the browser doesn't evict large bucket data
// and grants a higher quota threshold. Best-effort — not all browsers support it.
async function _requestPersistentStorage() {
   if (_persistRequested) return;
   _persistRequested = true;
   try {
      if (navigator.storage && navigator.storage.persist) {
         const isPersisted = await navigator.storage.persisted();
         if (!isPersisted) {
            const granted = await navigator.storage.persist();
            console.log('[bucket] persistent storage:', granted ? 'granted' : 'denied');
         }
      }
   } catch (_) {}
}

async function _ensureCompatibleVersion() {
   // Check current version AND that bucketId index exists; if not, delete DB
   return new Promise((resolve) => {
      const req = indexedDB.open(DB_NAME);
      req.onsuccess = () => {
         const db = req.result;
         const version = db.version;
         let hasIndex = false;
         if (db.objectStoreNames.contains(S_ITEMS)) {
            const tx = db.transaction(S_ITEMS, 'readonly');
            const store = tx.objectStore(S_ITEMS);
            hasIndex = store.indexNames.contains('bucketId');
         }
         db.close();
         // Handle both lower and higher versions - recreate if incompatible
         if (version < 2 || version > DB_VERSION || !hasIndex) {
            const del = indexedDB.deleteDatabase(DB_NAME);
            del.onsuccess = () => resolve();
            del.onerror = () => resolve(); // best effort
         } else {
            resolve();
         }
      };
      req.onerror = () => resolve(); // DB doesn't exist yet, fine
   });
}

function _doOpen() {
   return new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = (e) => {
         const db  = req.result;
         const old = e.oldVersion;
         if (old < 1) {
            const store = db.createObjectStore(S_ITEMS, { keyPath: 'id' });
            store.createIndex('source',   'source',   { unique: false });
            store.createIndex('addedAt',  'addedAt',  { unique: false });
            store.createIndex('bucketId', 'bucketId', { unique: false });
         }
         if (old >= 1 && old < 2) {
            // Add bucketId index to existing items store and migrate records
            const store = req.transaction.objectStore(S_ITEMS);
            if (!store.indexNames.contains('bucketId')) {
               store.createIndex('bucketId', 'bucketId', { unique: false });
            }
            store.openCursor().onsuccess = function () {
               const cursor = this.result;
               if (!cursor) return;
               const val = cursor.value;
               if (!val.bucketId) { val.bucketId = DEFAULT_ID; cursor.update(val); }
               cursor.continue();
            };
         }
         if (old < 2) {
            // guard: S_BUCKETS may already exist if only items needed upgrade
            if (!db.objectStoreNames.contains(S_BUCKETS)) {
               db.createObjectStore(S_BUCKETS, { keyPath: 'id' });
            }
         }
      };
      req.onblocked = () => {
         // another tab has the old version open; nothing we can do except wait
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror  = () => reject(req.error);
   });
}

function openDb() {
   if (_dbPromise) return _dbPromise;
   _dbPromise = _requestPersistentStorage()
      .then(() => _ensureCompatibleVersion())
      .then(() => _doOpen());
   return _dbPromise;
}

// ---- Low-level helpers -------------------------------------------------------
// Serialize DB writes to avoid concurrent readwrite transaction conflicts.
// If a write stalls (transaction never completes), reset the DB connection
// so subsequent writes don't block forever.
let _dbWriteQueue = Promise.resolve();
let _dbConn = null;

function serializeDbWrite(fn) {
   const run = _dbWriteQueue.then(fn, fn);
   _dbWriteQueue = run.catch(() => {});
   return run;
}

function _resetDbConn() {
   if (_dbConn) { try { _dbConn.close(); } catch (_) {} _dbConn = null; }
   _dbPromise = null;
}

function _txWrite(storeName, actionFn) {
   return new Promise((res, rej) => {
      let settled = false;
      const db = _dbConn;
      if (!db) { rej(new Error('DB not open')); return; }
      const tx = db.transaction(storeName, 'readwrite');
      actionFn(tx.objectStore(storeName));
      tx.oncomplete = () => { settled = true; res(); };
      tx.onerror    = () => { settled = true; rej(tx.error); };
      tx.onabort    = () => { settled = true; rej(tx.error || new Error('tx aborted')); };
      // Safety timeout: if the transaction doesn't settle in 5s, abort and reject
      setTimeout(() => {
         if (!settled) {
            try { tx.abort(); } catch (_) {}
            _resetDbConn();
            rej(new Error('DB write timeout'));
         }
      }, 5000);
   });
}

// On QuotaExceeded, try to free space by clearing the media cache, then retry
async function _clearMediaCacheAndRetry() {
   try {
      const mc = require('../../common/media-cache.cjs');
      if (mc && mc.clearCache) {
         console.log('[bucket] Quota exceeded — clearing media cache to free space');
         await mc.clearCache();
         return true;
      }
   } catch (_) {}
   return false;
}

// Strip non-cloneable / internal fields (underscore-prefixed, e.g. _canvas,
// _cancelled) before writing to IDB. Returns a shallow copy safe for structured clone.
function _sanitizeForIDB(item) {
   const out = {};
   for (const k of Object.keys(item)) {
      if (k.startsWith('_')) continue;          // internal/temp fields
      const v = item[k];
      if (v && typeof v === 'object' && v.nodeType) continue; // DOM nodes
      out[k] = v;
   }
   return out;
}

export async function dbPutItem(item) {
   return serializeDbWrite(async () => {
      if (!_dbConn) _dbConn = await openDb();
      const clean = _sanitizeForIDB(item);
      try {
         await _txWrite(S_ITEMS, (store) => store.put(clean));
      } catch (e) {
         console.error('[dbPutItem] write failed, resetting DB conn:', e);
         _resetDbConn();
         // If quota exceeded, try clearing media cache and retry once
         if (e && /quota|exceeded/i.test(String(e.message || e))) {
            const freed = await _clearMediaCacheAndRetry();
            if (freed) {
               _dbConn = await openDb();
               await _txWrite(S_ITEMS, (store) => store.put(clean));
               return;
            }
         }
         throw e;
      }
   });
}

async function dbGetByBucket(bucketId) {
   const db = await openDb();
   return new Promise((res, rej) => {
      const tx  = db.transaction(S_ITEMS, 'readonly');
      const idx = tx.objectStore(S_ITEMS).index('bucketId');
      const req = idx.getAll(IDBKeyRange.only(bucketId));
      req.onsuccess = () => res(req.result || []);
      req.onerror   = () => rej(req.error);
   });
}

// Exported for polling in xtrc.js (kept for compat — now returns active bucket items)
export async function dbGetAll(bucketId = DEFAULT_ID) {
   return dbGetByBucket(bucketId);
}

async function dbDeleteItem(id) {
   return serializeDbWrite(async () => {
      if (!_dbConn) _dbConn = await openDb();
      try { await _txWrite(S_ITEMS, (store) => store.delete(id)); }
      catch (e) { _resetDbConn(); throw e; }
   });
}

async function dbClearBucket(bucketId) {
   return serializeDbWrite(async () => {
      const all = await dbGetByBucket(bucketId);
      if (!_dbConn) _dbConn = await openDb();
      try { await _txWrite(S_ITEMS, (store) => { for (const it of all) store.delete(it.id); }); }
      catch (e) { _resetDbConn(); throw e; }
   });
}

async function dbDeleteBucketItems(bucketId) {
   return dbClearBucket(bucketId);
}

// ---- BucketManager ----------------------------------------------------------
async function dbPutBucket(bkt) {
   return serializeDbWrite(async () => {
      if (!_dbConn) _dbConn = await openDb();
      try { await _txWrite(S_BUCKETS, (store) => store.put(bkt)); }
      catch (e) { _resetDbConn(); throw e; }
   });
}

async function dbGetAllBuckets() {
   const db = await openDb();
   return new Promise((res, rej) => {
      const req = db.transaction(S_BUCKETS, 'readonly').objectStore(S_BUCKETS).getAll();
      req.onsuccess = () => res(req.result || []);
      req.onerror   = () => rej(req.error);
   });
}

async function dbDeleteBucket(id) {
   return serializeDbWrite(async () => {
      if (!_dbConn) _dbConn = await openDb();
      try { await _txWrite(S_BUCKETS, (store) => store.delete(id)); }
      catch (e) { _resetDbConn(); throw e; }
   });
}

export const BucketManager = {
   async list() {
      let rows = await dbGetAllBuckets();
      if (!rows.length) {
         const def = { id: DEFAULT_ID, name: 'default', createdAt: Date.now(), isOpen: true };
         await dbPutBucket(def);
         rows = [def];
      }
      // Ensure default is always open
      rows.forEach((b) => { if (b.id === DEFAULT_ID) b.isOpen = true; });
      return rows.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
   },

   async create(name) {
      const id  = `bkt_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const bkt = { id, name: name || 'New bucket', createdAt: Date.now(), isOpen: true };
      await dbPutBucket(bkt);
      return bkt;
   },

   async rename(id, newName) {
      const rows = await dbGetAllBuckets();
      const bkt  = rows.find((b) => b.id === id);
      if (!bkt) return;
      bkt.name = newName;
      await dbPutBucket(bkt);
   },

   async close(id) {
      if (id === DEFAULT_ID) return; // can't close default
      const rows = await dbGetAllBuckets();
      const bkt  = rows.find((b) => b.id === id);
      if (!bkt) return;
      bkt.isOpen = false;
      await dbPutBucket(bkt);
   },

   async open(id) {
      const rows = await dbGetAllBuckets();
      const bkt  = rows.find((b) => b.id === id);
      if (!bkt) return;
      bkt.isOpen = true;
      await dbPutBucket(bkt);
   },

   async remove(id) {
      if (id === DEFAULT_ID) return; // can't delete default
      await dbDeleteBucketItems(id);
      await dbDeleteBucket(id);
   },
};

// ---- Misc helpers -----------------------------------------------------------
export async function toArrayBuffer(input) {
   if (!input) return null;
   if (input instanceof ArrayBuffer) return input;
   if (ArrayBuffer.isView(input)) {
      // Check for detached ArrayBuffer (e.g. after ffmpeg.writeFile transfer)
      if (input.buffer.byteLength === 0) return null;
      return input.buffer.slice(input.byteOffset, input.byteOffset + input.byteLength);
   }
   if (input instanceof Blob) return await input.arrayBuffer();
   if (typeof input === 'string') {
      const resp = await fetch(input);
      return await resp.arrayBuffer();
   }
   return null;
}

// ---- Bucket item context menu ----
let _bucketContextMenuEl = null;

function _closeBucketContextMenu() {
   if (_bucketContextMenuEl) {
      _bucketContextMenuEl.remove();
      _bucketContextMenuEl = null;
   }
}

function _showBucketContextMenu(x, y, item, bucketService) {
   _closeBucketContextMenu();
   const menu = document.createElement('div');
   menu.className = 'bucket-context-menu';
   menu.style.position = 'fixed';
   menu.style.left = x + 'px';
   menu.style.top = y + 'px';
   menu.style.zIndex = '100000';

   // Download option
   const dlBtn = document.createElement('div');
   dlBtn.className = 'bucket-ctx-item';
   dlBtn.textContent = 'Download';
   dlBtn.addEventListener('click', () => {
      _closeBucketContextMenu();
      _downloadBucketItem(item);
   });
   menu.appendChild(dlBtn);

   // Remove option
   const rmBtn = document.createElement('div');
   rmBtn.className = 'bucket-ctx-item';
   rmBtn.textContent = 'Remove';
   rmBtn.addEventListener('click', () => {
      _closeBucketContextMenu();
      bucketService.remove(item.id);
   });
   menu.appendChild(rmBtn);

   // Duplicate option
   const dupBtn = document.createElement('div');
   dupBtn.className = 'bucket-ctx-item';
   dupBtn.textContent = 'Duplicate';
   dupBtn.addEventListener('click', async () => {
      _closeBucketContextMenu();
      const uniqueName = bucketService.getUniqueName(item.source, item.name);
      const newId = makeId(bucketService.bucketId, item.source, uniqueName);
      const newItem = { ...item, id: newId, name: uniqueName, addedAt: Date.now() };
      bucketService.items.push(newItem);
      try { await dbPutItem(newItem); } catch (e) { }
      bucketService.render();
      bucketService.setStatus(`+ ${uniqueName}`);
   });
   menu.appendChild(dupBtn);

   document.body.appendChild(menu);
   _bucketContextMenuEl = menu;

   // Close on click outside or Escape
   setTimeout(() => {
      document.addEventListener('click', _closeBucketContextMenu, { once: true });
      document.addEventListener('keydown', function escClose(e) {
         if (e.key === 'Escape') { _closeBucketContextMenu(); document.removeEventListener('keydown', escClose); }
      });
   }, 0);
}

function _downloadBucketItem(item) {
   if (!item || !item.wav) return;
   let mime = 'audio/wav';
   if (item.type === 'midi') mime = 'audio/midi';
   else if (item.type === 'video') mime = item.name.endsWith('.webm') ? 'video/webm' : 'video/mp4';
   else if (item.type === 'image') {
      const ext = (item.name.split('.').pop() || '').toLowerCase();
      mime = { png:'image/png', jpg:'image/jpeg', jpeg:'image/jpeg', gif:'image/gif',
               bmp:'image/bmp', webp:'image/webp', tiff:'image/tiff', tga:'image/x-tga',
               pcx:'image/x-pcx', psd:'image/x-psd', ico:'image/x-icon' }[ext] || 'application/octet-stream';
   } else if (item.type === 'module' || item.type === 'archive' || item.type === 'raw') {
      mime = 'application/octet-stream';
   }
   const blob = new Blob([item.wav], { type: mime });
   const url = URL.createObjectURL(blob);
   const a = document.createElement('a');
   a.href = url;
   a.download = item.name;
   document.body.appendChild(a);
   a.click();
   a.remove();
   setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export const SOURCE_LABEL = {
   mod: 'MOD', sf2: 'SF2', akai: 'AKAI', roland: 'ROL', rbm: 'RBM',
   emu: 'EMU', korg: 'KORG', electribe: 'ELECTRIBE', ensoniq: 'ENSONIQ',
   yamaha: 'YAMAHA', raw: 'RAW', dbm: 'DBM', sfz: 'SFZ', rex: 'REX',
   casio: 'CASIO', game: 'GAME', kontakt: 'KONTAKT', recycle: 'RECYCLE',
   gig: 'GIG', kurzweil: 'KURZWEIL', fairlight: 'FAIRLIGHT',
};

export function fmtSize(bytes) {
   if (!bytes) return '0 B';
   const k = 1024;
   const names = ['B', 'KB', 'MB', 'GB'];
   const i = Math.floor(Math.log(bytes) / Math.log(k));
   return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + names[i];
}

// ---- Waveform drawing -------------------------------------------------------
export function drawWaveformFromWav(canvas, wavBuffer, pcm, sampleRate, channels, bitDepth) {
   // If PCM data is available, use shared drawWaveformFromInt16
   if (pcm && pcm.length) {
      const bitDepthToUse = bitDepth || (pcm instanceof Int32Array ? 24 : 16);
      drawWaveformFromInt16(canvas, pcm, channels || 1, bitDepthToUse);
      return;
   }

   // Fallback to WAV parsing if no PCM data - extract PCM and use drawWaveformFromInt16
   // for unified waveform rendering across all adapters
   const colors = getThemeDrawColors();
   const ctx = canvas.getContext('2d');
   const W = canvas.width, H = canvas.height;
   ctx.fillStyle = colors.bg;
   ctx.fillRect(0, 0, W, H);
   if (!wavBuffer || wavBuffer.byteLength < 44) {
      ctx.fillStyle = colors.mid;
      ctx.fillRect(0, H / 2 - 1, W, 2);
      return;
   }
   try {
      // Normalize to ArrayBuffer for DataView
      const ab = wavBuffer instanceof ArrayBuffer ? wavBuffer
         : (wavBuffer.buffer ? wavBuffer.buffer.slice(wavBuffer.byteOffset || 0, (wavBuffer.byteOffset || 0) + wavBuffer.byteLength) : wavBuffer);
      const dv = new DataView(ab);
      const numChannels = dv.getUint16(22, true);
      const bitsPerSample = dv.getUint16(34, true);
      const formatTag = dv.getUint16(20, true); // 1=PCM, 3=IEEE_FLOAT
      let dataOffset = 12;
      while (dataOffset + 8 < ab.byteLength) {
         const tag = String.fromCharCode(
            dv.getUint8(dataOffset), dv.getUint8(dataOffset + 1),
            dv.getUint8(dataOffset + 2), dv.getUint8(dataOffset + 3)
         );
         const chunkSize = dv.getUint32(dataOffset + 4, true);
         if (tag === 'data') { dataOffset += 8; break; }
         dataOffset += 8 + chunkSize;
      }
      const bytesPerSample = bitsPerSample / 8;
      const frameCount = Math.floor((ab.byteLength - dataOffset) / (bytesPerSample * numChannels));
      if (!frameCount) return;

      // Extract PCM into Int16Array (normalized) and use drawWaveformFromInt16
      // for the same min/max per column + auto-scale algorithm as adapters
      let pcm;
      if (bitsPerSample === 16) {
         pcm = new Int16Array(ab, dataOffset, frameCount * numChannels);
      } else if (bitsPerSample === 8) {
         pcm = new Int16Array(frameCount * numChannels);
         for (let i = 0; i < frameCount * numChannels; i++) {
            pcm[i] = (dv.getUint8(dataOffset + i) - 128) << 8;
         }
      } else if (bitsPerSample === 24) {
         pcm = new Int16Array(frameCount * numChannels);
         for (let i = 0; i < frameCount * numChannels; i++) {
            const byteIdx = dataOffset + i * 3;
            const b0 = dv.getUint8(byteIdx), b1 = dv.getUint8(byteIdx + 1), b2 = dv.getUint8(byteIdx + 2);
            let s = (b2 << 16) | (b1 << 8) | b0;
            if (s & 0x800000) s -= 0x1000000;
            pcm[i] = s >> 8; // down-scale 24-bit to 16-bit
         }
      } else if (bitsPerSample === 32 && formatTag === 3) {
         // 32-bit IEEE float (format tag 3) — used by sf2 and other adapters
         pcm = new Int16Array(frameCount * numChannels);
         for (let i = 0; i < frameCount * numChannels; i++) {
            const f = dv.getFloat32(dataOffset + i * 4, true);
            pcm[i] = Math.max(-32768, Math.min(32767, Math.round(f * 32767)));
         }
      } else if (bitsPerSample === 32) {
         pcm = new Int16Array(frameCount * numChannels);
         for (let i = 0; i < frameCount * numChannels; i++) {
            const byteIdx = dataOffset + i * 4;
            pcm[i] = dv.getInt32(byteIdx, true) >> 16; // down-scale 32-bit to 16-bit
         }
      } else if (bitsPerSample === 64 && formatTag === 3) {
         // 64-bit IEEE float
         pcm = new Int16Array(frameCount * numChannels);
         for (let i = 0; i < frameCount * numChannels; i++) {
            const f = dv.getFloat64(dataOffset + i * 8, true);
            pcm[i] = Math.max(-32768, Math.min(32767, Math.round(f * 32767)));
         }
      } else {
         return;
      }
      drawWaveformFromInt16(canvas, pcm, numChannels, 16);
   } catch (_) {
      ctx.strokeStyle = colors.mid;
      ctx.beginPath();
      ctx.moveTo(0, H / 2);
      ctx.lineTo(W, H / 2);
      ctx.stroke();
   }
}

export function makeId(bucketId, source, name) { return `${bucketId}::${source}::${name}`; }

// ---- BucketService ----------------------------------------------------------
export class BucketService {
   constructor({ container, statusFn, panelEl, countEl, bucketId, confirmFn } = {}) {
      this.itemsDiv  = container;
      this.panelEl   = panelEl  || null;
      this.countEl   = countEl  || null;
      this.setStatus = statusFn || (() => {});
      this.confirmFn = confirmFn || null; // async confirm function(message) => boolean
      this.bucketId  = bucketId || DEFAULT_ID;
      this.items     = [];
      this.kitIdx    = 0;
      this.isPlaying = false; // Track if bucket is currently playing
      // View mode: 'grid' (canvas thumbnails) or 'list' (compact text rows)
      try {
         this.viewMode = localStorage.getItem('bucket-view-mode') || 'grid';
         if (this.viewMode !== 'grid' && this.viewMode !== 'list') this.viewMode = 'grid';
      } catch (_) { this.viewMode = 'grid'; }
      
      // Listen to stop bucket-wav-play events (for fallback WAV playback)
      window.addEventListener('bucket-wav-stop', () => {
         const playingCanvases = this.itemsDiv.querySelectorAll('.bucketitem-waveform.playing');
         playingCanvases.forEach(canvas => {
            canvas.classList.remove('playing');
         });
         this.isPlaying = false;
      });
      this._stopWavHandler = () => {
         // Remove playing class from all canvases
         if (this.itemsDiv) {
            const playingCanvases = this.itemsDiv.querySelectorAll('.bucketitem-waveform.playing');
            playingCanvases.forEach(canvas => {
               canvas.classList.remove('playing');
            });
         }
      };
      window.addEventListener('bucket-wav-stop', this._stopWavHandler);
      
      // Also listen to stop-all events
      this._stopAllHandler = () => this._stopWavHandler();
      window.addEventListener('bucket-stop-all', this._stopAllHandler);
   }

   switchBucket(id) {
      this.bucketId = id || DEFAULT_ID;
      return this.load();
   }

   async load() {
      try { this.items = await dbGetByBucket(this.bucketId) || []; }
      catch (e) { this.items = []; }
      this.render();
   }

   has(source, name) {
      return !!this.items.find((it) => it.id === makeId(this.bucketId, source, name));
   }

   /**
    * Generate a unique name by appending a number if the name already exists
    * e.g., "sample.wav" -> "sample (1).wav" -> "sample (2).wav"
    */
   getUniqueName(source, name) {
      if (!this.has(source, name)) return name;
      
      // Extract base name and extension
      const lastDot = name.lastIndexOf('.');
      const baseName = lastDot > 0 ? name.slice(0, lastDot) : name;
      const ext = lastDot > 0 ? name.slice(lastDot) : '';
      
      let counter = 1;
      let newName;
      do {
         newName = `${baseName} (${counter})${ext}`;
         counter++;
      } while (this.has(source, newName));
      
      return newName;
   }

   async add({ source, name, wavLike, url, midiLike, fileType, pcm, sampleRate, channels, bitDepth, videoData, videoFormat, imageData, imageFormat, imageMime }) {
      if (!source || !name) return false;
      try {

      // Auto-rename if name already exists
      const uniqueName = this.getUniqueName(source, name);
      const id = makeId(this.bucketId, source, uniqueName);

      // Determine file type and data
      let data = null;
      let type = fileType || 'wav';

      if (midiLike) {
         data = await toArrayBuffer(midiLike);
         type = 'midi';
      } else if (type === 'video') {
         // Video file — store the video data, optionally convert to H264 if not browser-playable
         data = await toArrayBuffer(videoData || wavLike || url);
         // If videoData was detached (e.g. after ffmpeg transfer), fall back to URL fetch
         if (!data && wavLike) data = await toArrayBuffer(wavLike);
         if (!data && url) data = await toArrayBuffer(url);
         const ext = (name.split('.').pop() || '').toLowerCase();

         // Browser-playable video formats: mp4 (H264), webm (VP8/VP9/Opus), ogg (Theora)
         const browserPlayable = ['mp4', 'webm', 'ogv', 'mov', 'm4v'];
         const needsConversion = !browserPlayable.includes(ext);

         if (needsConversion && data && data.byteLength < 200 * 1024 * 1024) {
            // Convert to WebM (VP8/libvorbis) via ffmpeg — more stable in WASM than libx264
            // Keep a copy of original data — ffmpeg.writeFile() detaches the ArrayBuffer
            const originalData = data;
            const baseName = uniqueName.replace(/\.[^.]+$/, '');
            const webmName = baseName + '.webm';
            const webmId = makeId(this.bucketId, source, webmName);
            console.log(`[BUCKET video] needsConversion=true ext=${ext} size=${data.byteLength} name=${uniqueName}`);

            // ---- Add placeholder card immediately so the user sees progress ----
            const encItem = {
               id: webmId,
               bucketId: this.bucketId,
               source,
               name: webmName,
               addedAt: Date.now(),
               wav: null,
               type: 'video',
               encoding: true,
               encodingProgress: 0,
            };
            this.items.push(encItem);
            this.render();
            this.setStatus(`Converting ${uniqueName} to WebM...`);

            // Indeterminate animation timer — redraws the card while progress is 0
            // (before ffmpeg emits any time= lines). Stops once progress > 0 or encoding ends.
            const animTimer = setInterval(() => {
               if (encItem._cancelled || !encItem.encoding) { clearInterval(animTimer); return; }
               if ((encItem.encodingProgress || 0) > 0) { clearInterval(animTimer); return; }
               this._updateEncodingCard(encItem, 0);
            }, 120);

            try {
               const ffmpeg = (typeof window !== 'undefined' && window.getFFmpeg) ? await window.getFFmpeg() : (window.ffmpeg || null);
               console.log(`[BUCKET video] ffmpeg instance:`, ffmpeg ? 'available' : 'NULL');
               if (ffmpeg) {
                  const inputName = `input_${Date.now()}.${ext}`;
                  // Copy data before passing to ffmpeg — writeFile transfers (detaches) the buffer
                  const copyForFfmpeg = new Uint8Array(originalData.byteLength);
                  copyForFfmpeg.set(new Uint8Array(originalData));
                  console.log(`[BUCKET video] writeFile: ${inputName} (${copyForFfmpeg.byteLength} bytes)`);
                  await ffmpeg.writeFile(inputName, copyForFfmpeg);
                  console.log(`[BUCKET video] writeFile OK`);
                  // Convert to WebM with VP8 codec — more stable in WASM than libx264
                  // VP8/libvpx is well-supported and browsers can play WebM natively
                  const outputName = `output_${Date.now()}.webm`;
                  const args = ['-y', '-i', inputName, '-c:v', 'libvpx', '-b:v', '1M', '-c:a', 'libvorbis', outputName];
                  console.log(`[BUCKET video] ffmpeg args: ${args.join(' ')}`);

                  // Parse ffmpeg stderr for reliable progress tracking.
                  // ffmpeg.wasm's 'progress' event is unreliable in WASM (often doesn't fire
                  // because internal duration detection fails). Instead, we listen to 'log'
                  // events and parse "Duration: HH:MM:SS.ms" (from input header) and
                  // "time=HH:MM:SS.ms" (from progress lines) to compute progress ourselves.
                  let totalDuration = 0;
                  const parseTime = (s) => {
                     const m = s.match(/(\d+):(\d+):(\d+(?:\.\d+)?)/);
                     if (!m) return 0;
                     return (+m[1]) * 3600 + (+m[2]) * 60 + (+m[3]);
                  };
                  const logHandler = ({ type, message }) => {
                     if (encItem._cancelled) return;
                     if (type !== 'stderr' || !message) return;
                     // Capture total duration from input header
                     if (!totalDuration) {
                        const dm = message.match(/Duration:\s*(\d+:\d+:\d+(?:\.\d+)?)/);
                        if (dm) {
                           totalDuration = parseTime(dm[0]);
                           console.log(`[BUCKET video] duration detected: ${totalDuration}s`);
                        }
                     }
                     // Parse current time from progress lines
                     const tm = message.match(/time=\s*(\d+:\d+:\d+(?:\.\d+)?)/);
                     if (tm && totalDuration > 0) {
                        const pct = Math.max(0, Math.min(1, parseTime(tm[0]) / totalDuration));
                        encItem.encodingProgress = pct;
                        this._updateEncodingCard(encItem, pct);
                     }
                  };
                  // Also keep the native progress event as a fallback
                  const progressHandler = ({ progress }) => {
                     if (encItem._cancelled) return;
                     if (typeof progress !== 'number' || isNaN(progress)) return;
                     const pct = Math.max(0, Math.min(1, progress));
                     if (pct > (encItem.encodingProgress || 0)) {
                        encItem.encodingProgress = pct;
                        this._updateEncodingCard(encItem, pct);
                     }
                  };
                  try { ffmpeg.on('log', logHandler); } catch (_) {}
                  try { ffmpeg.on('progress', progressHandler); } catch (_) {}

                  try {
                     console.log(`[BUCKET video] exec starting...`);
                     if (typeof window !== 'undefined' && window.runFFmpeg) {
                        const result = await window.runFFmpeg(args);
                        console.log(`[BUCKET video] runFFmpeg result:`, result);
                     } else {
                        const result = await ffmpeg.exec(args);
                        console.log(`[BUCKET video] ffmpeg.exec result:`, result);
                     }
                     console.log(`[BUCKET video] exec completed OK`);
                  } catch (execErr) {
                     console.error(`[BUCKET video] exec FAILED:`, execErr);
                     console.error(`[BUCKET video] stderr:`, execErr?._ffmpegStderr || '(none)');
                     throw execErr;
                  } finally {
                     try { ffmpeg.off('log', logHandler); } catch (_) {}
                     try { ffmpeg.off('progress', progressHandler); } catch (_) {}
                  }

                  // If user cancelled during encoding, remove placeholder and bail
                  if (encItem._cancelled) {
                     console.log(`[BUCKET video] cancelled by user`);
                     this.items = this.items.filter((it) => it.id !== webmId);
                     this.render();
                     return false;
                  }

                  console.log(`[BUCKET video] readFile: ${outputName}`);
                  const outputData = await ffmpeg.readFile(outputName);
                  console.log(`[BUCKET video] readFile OK: ${outputData.byteLength} bytes`);
                  try { await ffmpeg.deleteFile(inputName); } catch (_) {}
                  try { await ffmpeg.deleteFile(outputName); } catch (_) {}
                  const convertedData = outputData.buffer.slice(outputData.byteOffset, outputData.byteOffset + outputData.byteLength);

                  // Replace placeholder with the converted item
                  encItem.encoding = false;
                  encItem.wav = convertedData;
                  encItem.encodingProgress = 1;
                  // Strip non-cloneable canvas ref before IDB write
                  const canvasRef = encItem._canvas;
                  delete encItem._canvas;
                  try { await dbPutItem(encItem); } catch (e) { console.error(`[BUCKET video] dbPutItem failed:`, e); }
                  encItem._canvas = canvasRef;
                  // Transform the encoding card in-place — no full re-render
                  this._finalizeEncodingCard(encItem);
                  this.setStatus(`+ ${webmName} (converted)`);
                  console.log(`[BUCKET video] conversion complete: ${webmName}`);
                  return true;
               } else {
                  // No ffmpeg available — remove placeholder, fall through to store original
                  console.warn(`[BUCKET video] no ffmpeg available, storing original`);
                  clearInterval(animTimer);
                  this.items = this.items.filter((it) => it.id !== webmId);
                  this.render();
               }
            } catch (e) {
               clearInterval(animTimer);
               console.error(`[BUCKET video] conversion error:`, e);
               console.error(`[BUCKET video] error stderr:`, e?._ffmpegStderr || '(none)');
               console.error(`[BUCKET video] error cmd:`, e?._ffmpegCmd || '(none)');
               if (e?._ffmpegStderr)
               this.setStatus(`Conversion failed, keeping original ${uniqueName}`);
               // If ffmpeg WASM crashed (RuntimeError), reset the instance so it can be reloaded
               const isWasmCrash = (e?.name === 'RuntimeError') || /RuntimeError|out of bounds/i.test(String(e?.message || e));
               if (isWasmCrash && typeof window !== 'undefined' && window.resetFFmpeg) window.resetFFmpeg();
               // Remove the placeholder card — we'll fall through and store the original
               if (!encItem._cancelled) {
                  this.items = this.items.filter((it) => it.id !== webmId);
                  this.render();
               }
               // Restore original data — it may have been detached by ffmpeg
               data = originalData;
               // If original was detached, try re-fetching from URL
               if (!data || data.byteLength === 0) {
                  data = await toArrayBuffer(wavLike || url);
               }
            }
         }
      } else if (type === 'image') {
         // Image file — store the image data
         data = await toArrayBuffer(imageData || wavLike || url);
         if (!data && wavLike) data = await toArrayBuffer(wavLike);
         if (!data && url) data = await toArrayBuffer(url);
      } else {
         data = await toArrayBuffer(wavLike || url);
         // Keep 'module' type if specified, otherwise default to 'wav'
         if (type !== 'module') type = 'wav';
      }

      if (!data || !data.byteLength) {
         this.setStatus(`add failed: ${uniqueName} (no data)`);
         return false;
      }

      const item = { id, bucketId: this.bucketId, source, name: uniqueName, addedAt: Date.now(), wav: data, type, pcm, sampleRate, channels, bitDepth };
      try { await dbPutItem(item); } catch (e) {
         console.error('[BUCKET.add] IDB error:', e);
         this.setStatus(`add failed: ${uniqueName} (${e.message || 'IDB error'})`);
         return false;
      }
      this.items.push(item);
      this.render();
      this.setStatus(`+ ${uniqueName}`);
      return true;
      } catch (err) { console.error('[BUCKET.add] FAILED:', err); return false; }
   }

   async remove(idOrSource, name) {
      const id = name ? makeId(this.bucketId, idOrSource, name) : idOrSource;
      this.items = this.items.filter((it) => it.id !== id);
      try { await dbDeleteItem(id); } catch (e) { }
      this.render();
   }

   // Move an item from the current bucket to a different bucket
   async moveItem(itemId, targetBucketId) {
      const item = this.items.find((it) => it.id === itemId);
      if (!item) return false;
      if (targetBucketId === this.bucketId) return false;

      // Remove from current bucket (memory + IDB)
      this.items = this.items.filter((it) => it.id !== itemId);
      try { await dbDeleteItem(itemId); } catch (e) { }
      this.render();

      // Create new item in target bucket with a unique name
      const targetItems = await dbGetByBucket(targetBucketId);
      const existingNames = new Set(targetItems.map((it) => it.name));
      let newName = item.name;
      if (existingNames.has(newName)) {
         const lastDot = newName.lastIndexOf('.');
         const base = lastDot > 0 ? newName.slice(0, lastDot) : newName;
         const ext = lastDot > 0 ? newName.slice(lastDot) : '';
         let i = 1;
         while (existingNames.has(`${base} (${i})${ext}`)) i++;
         newName = `${base} (${i})${ext}`;
      }
      const newId = makeId(targetBucketId, item.source, newName);
      const newItem = { ...item, id: newId, bucketId: targetBucketId, name: newName, addedAt: Date.now() };
      try { await dbPutItem(newItem); } catch (e) { }
      this.setStatus(`moved ${item.name} → ${newName}`);
      return true;
   }

   async clear() {
      this.items = [];
      try { await dbClearBucket(this.bucketId); } catch (e) { }
      this.render();
      this.setStatus('bucket cleared');
   }

   // Listen for stop-all events (e.g., when another audio starts)
   stopAllPlayback() {
      // If we're the one dispatching, skip (prevent self-stop)
      if (this.isDispatchingStop) return;
      
      // If we're not playing, nothing to do
      if (!this.isPlaying) return;
      
      // Stop any currently playing audio from bucket items
      const playingCanvases = this.itemsDiv.querySelectorAll('.bucketitem-waveform.playing');
      playingCanvases.forEach(canvas => {
         canvas.classList.remove('playing');
      });
      
      // Don't call stopAllAudioPlayback here - the audio is already stopped
      // by the component that dispatched the event (via playAudioBuffer -> audioManager.stop)
      // We just need to clear our visual state
      this.isPlaying = false;
   }

   // Switch bucket view mode ('grid' or 'list'). Persists to localStorage.
   setViewMode(mode) {
      if (mode !== 'grid' && mode !== 'list') return;
      if (this.viewMode === mode) return;
      this.viewMode = mode;
      try { localStorage.setItem('bucket-view-mode', mode); } catch (_) {}
      // Update container class for CSS
      if (this.itemsDiv) {
         this.itemsDiv.classList.toggle('bucket-list-view', mode === 'list');
         this.itemsDiv.classList.toggle('bucket-grid-view', mode === 'grid');
      }
      // Update toggle button state if present
      const toggleBtn = document.getElementById('bucket-view-toggle');
      if (toggleBtn) {
         toggleBtn.textContent = mode === 'list' ? '\u2630' : '\u25A6';
         toggleBtn.title = mode === 'list' ? 'Switch to grid view' : 'Switch to list view';
         toggleBtn.setAttribute('aria-pressed', mode === 'list' ? 'true' : 'false');
      }
      this.render();
   }

   render() {
      if (!this.itemsDiv) return;
      this.itemsDiv.innerHTML = '';
      // Apply view mode class to container
      this.itemsDiv.classList.toggle('bucket-list-view', this.viewMode === 'list');
      this.itemsDiv.classList.toggle('bucket-grid-view', this.viewMode === 'grid');
      const sorted = this.items.slice().sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));
      this.itemsDiv.setAttribute('role', 'list');
      this.itemsDiv.setAttribute('aria-label', 'Sample bucket');
      for (const item of sorted) {
         const row = document.createElement('div');
         row.classList.add('bucketitem');
         if (this.viewMode === 'list') row.classList.add('bucketitem-list');
         row.dataset.id = item.id;
         row.dataset.type = item.type || 'wav';
         row.setAttribute('role', 'listitem');
         row.setAttribute('aria-label', `${item.name}, ${item.type || 'wav'}`);

         // Create name label
         const label = document.createElement('label');
         label.textContent = item.name;

         // ---- List view mode: compact text row with play button ----
         if (this.viewMode === 'list') {
            // Type badge as text
            const typeBadge = document.createElement('span');
            typeBadge.className = 'bucketitem-type-badge';
            typeBadge.textContent = (item.type || 'wav').toUpperCase();
            row.appendChild(typeBadge);

            // Play button (for playable types)
            const isPlayable = item.type !== 'archive' && item.type !== 'raw' && !item.encoding;
            if (isPlayable) {
               const playBtn = document.createElement('button');
               playBtn.className = 'bucketitem-play';
               playBtn.textContent = '\u25B6';
               playBtn.setAttribute('aria-label', `Play ${item.name}`);
               playBtn.addEventListener('click', () => {
                  if (item.type === 'midi') {
                     window.dispatchEvent(new CustomEvent('bucket-midi-play', {
                        detail: { name: item.name, data: item.wav, source: item.source }
                     }));
                  } else if (item.type === 'module') {
                     window.dispatchEvent(new CustomEvent('bucket-module-play', {
                        detail: { name: item.name, data: item.wav, source: item.source }
                     }));
                  } else if (item.type === 'video') {
                     const mimeType = item.name.endsWith('.webm') ? 'video/webm' : 'video/mp4';
                     const blob = new Blob([item.wav], { type: mimeType });
                     openVideoPlayer({ blob, name: item.name, downloadable: true, downloadName: item.name });
                  } else if (item.type === 'image') {
                     const ext = (item.name.split('.').pop() || '').toLowerCase();
                     const imgMime = {
                        png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
                        gif: 'image/gif', bmp: 'image/bmp', webp: 'image/webp',
                        tiff: 'image/tiff', tga: 'image/x-tga', pcx: 'image/x-pcx',
                     }[ext] || 'application/octet-stream';
                     const blob = new Blob([item.wav], { type: imgMime });
                     const url = URL.createObjectURL(blob);
                     if (window.openImageViewer) {
                        window.openImageViewer({ url, name: item.name, downloadable: true, downloadUrl: url, downloadName: item.name });
                     } else {
                        window.open(url, '_blank');
                        setTimeout(() => URL.revokeObjectURL(url), 60000);
                     }
                  } else {
                     // WAV — same playback logic as grid view
                     this.isPlaying = true;
                     stopAllAudioPlayback();
                     playBtn.classList.add('playing');
                     if (item.pcm && item.pcm.length && item.sampleRate) {
                        const scale = item.bitDepth === 24 ? 8388608 : (item.bitDepth === 32 ? 2147483648 : 32768);
                        const ctx = getGlobalAudioContext();
                        const audioBuffer = ctx.createBuffer(item.channels || 1, item.pcm.length, item.sampleRate);
                        if (item.channels === 1) {
                           const channelData = audioBuffer.getChannelData(0);
                           for (let i = 0; i < item.pcm.length; i++) channelData[i] = item.pcm[i] / scale;
                        } else if (item.channels === 2) {
                           const left = audioBuffer.getChannelData(0);
                           const right = audioBuffer.getChannelData(1);
                           for (let i = 0; i < item.pcm.length / 2; i++) {
                              left[i] = item.pcm[i * 2] / scale;
                              right[i] = item.pcm[i * 2 + 1] / scale;
                           }
                        }
                        playAudioBuffer(audioBuffer, ctx, 1, playBtn, () => {
                           this.isPlaying = false;
                           playBtn.classList.remove('playing');
                        }, 'bucket');
                     } else {
                        const event = new CustomEvent('bucket-wav-play', {
                           detail: { name: item.name, data: item.wav, pcm: item.pcm, sampleRate: item.sampleRate, channels: item.channels, bitDepth: item.bitDepth, canvas: playBtn }
                        });
                        window.dispatchEvent(event);
                        setTimeout(async () => {
                           if (window.__bucketWavPlayHandled) {
                              window.__bucketWavPlayHandled = false;
                              this.isPlaying = false;
                              playBtn.classList.remove('playing');
                              return;
                           }
                           try {
                              const ctx = getGlobalAudioContext();
                              const audioBuffer = await ctx.decodeAudioData(item.wav.slice(0));
                              playAudioBuffer(audioBuffer, ctx, 1, playBtn, () => {
                                 this.isPlaying = false;
                                 playBtn.classList.remove('playing');
                              }, 'bucket');
                           } catch (err) {
                              this.isPlaying = false;
                              playBtn.classList.remove('playing');
                           }
                        }, 50);
                     }
                  }
               });
               row.appendChild(playBtn);
            } else if (item.encoding) {
               // Encoding placeholder — show progress text
               const encLabel = document.createElement('span');
               encLabel.className = 'bucketitem-encoding-text';
               encLabel.textContent = `encoding… ${Math.round(item.encodingProgress || 0)}%`;
               row.appendChild(encLabel);
            }

            // Size info
            if (item.wav && item.wav.byteLength) {
               const sizeSpan = document.createElement('span');
               sizeSpan.className = 'bucketitem-size';
               sizeSpan.textContent = fmtSize(item.wav.byteLength);
               row.appendChild(sizeSpan);
            }

            // Name label
            label.classList.add('bucketitem-name-text');
            row.appendChild(label);

            // Skip canvas rendering — go straight to edit/remove buttons
            // (edit/remove/drag/contextmenu code is shared below)
         } else {
         // ---- Grid view mode: canvas thumbnails (original behavior) ----

         // Create visual based on file type
         if (item.type === 'midi') {
            // MIDI piano roll canvas
            const canvas = document.createElement('canvas');
            canvas.className = 'bucketitem-waveform';
            canvas.width = 160;
            canvas.height = 58;
            canvas.style.cursor = 'pointer';
            canvas.setAttribute('role', 'img');
            canvas.setAttribute('aria-label', `MIDI piano roll for ${item.name}`);
            drawMidiPianoRoll(canvas, item.wav);
            drawTypeBadge(canvas, 'MIDI');
            canvas.addEventListener('click', () => {
               // Dispatch custom event for MIDI playback
               const event = new CustomEvent('bucket-midi-play', {
                  detail: { name: item.name, data: item.wav, source: item.source }
               });
               window.dispatchEvent(event);
            });
            row.appendChild(canvas);
         } else if (item.type === 'module') {
            // Module tracker grid canvas
            const canvas = document.createElement('canvas');
            canvas.className = 'bucketitem-waveform';
            canvas.width = 160;
            canvas.height = 58;
            canvas.style.cursor = 'pointer';
            canvas.setAttribute('role', 'img');
            canvas.setAttribute('aria-label', `Tracker module grid for ${item.name}`);
            drawTrackerGrid(canvas, item.wav);
            drawTypeBadge(canvas, 'MOD');
            canvas.addEventListener('click', () => {
               // Dispatch custom event for module playback (handled by mod adapter)
               const event = new CustomEvent('bucket-module-play', {
                  detail: { name: item.name, data: item.wav, source: item.source }
               });
               window.dispatchEvent(event);
            });
            row.appendChild(canvas);
         } else if (item.type === 'archive' || item.type === 'raw') {
            // Archive/raw file icon canvas
            const canvas = document.createElement('canvas');
            canvas.className = 'bucketitem-waveform';
            canvas.width = 160;
            canvas.height = 58;
            canvas.setAttribute('role', 'img');
            canvas.setAttribute('aria-label', `${(item.type || 'raw').toUpperCase()} file icon for ${item.name}`);
            drawArchiveIcon(canvas);
            drawTypeBadge(canvas, (item.type || 'raw').toUpperCase());
            row.appendChild(canvas);
         } else if (item.type === 'video' && item.encoding) {
            // Video being encoded — show progress card (no click handler until done)
            const canvas = document.createElement('canvas');
            canvas.className = 'bucketitem-waveform encoding';
            canvas.width = 160;
            canvas.height = 58;
            canvas.setAttribute('role', 'img');
            canvas.setAttribute('aria-label', `Encoding ${item.name}, ${Math.round(item.encodingProgress || 0)} percent`);
            drawEncodingProgress(canvas, item.encodingProgress || 0, 'ENCODING');
            // Store canvas reference for lightweight progress updates
            item._canvas = canvas;
            row.appendChild(canvas);
         } else if (item.type === 'video') {
            // Video file — draw first frame, click to play in modal
            const canvas = document.createElement('canvas');
            canvas.className = 'bucketitem-waveform';
            canvas.width = 160;
            canvas.height = 58;
            canvas.style.cursor = 'pointer';
            canvas.setAttribute('role', 'img');
            canvas.setAttribute('aria-label', `Video thumbnail for ${item.name}, click to play`);
            // Draw first frame (async), fall back to icon
            const mimeType = item.name.endsWith('.webm') ? 'video/webm' : 'video/mp4';
            drawFirstFrame(item.wav, mimeType, canvas, null).then(ok => {
               if (!ok) drawVideoIcon(canvas);
               drawTypeBadge(canvas, 'VIDEO');
            });
            canvas.addEventListener('click', () => {
               const blob = new Blob([item.wav], { type: mimeType });
               openVideoPlayer({ blob, name: item.name, downloadable: true, downloadName: item.name });
            });
            row.appendChild(canvas);
         } else if (item.type === 'image') {
            // Image file — draw thumbnail, click to view in modal
            const canvas = document.createElement('canvas');
            canvas.className = 'bucketitem-waveform';
            canvas.width = 160;
            canvas.height = 58;
            canvas.style.cursor = 'pointer';
            canvas.setAttribute('role', 'img');
            canvas.setAttribute('aria-label', `Image thumbnail for ${item.name}, click to view`);
            // Draw image thumbnail (async), fall back to icon
            const ext = (item.name.split('.').pop() || '').toLowerCase();
            const imgMime = {
               png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
               gif: 'image/gif', bmp: 'image/bmp', webp: 'image/webp',
               tiff: 'image/tiff', tga: 'image/x-tga', pcx: 'image/x-pcx',
               psd: 'image/x-psd', ico: 'image/x-icon',
            }[ext] || 'application/octet-stream';
            drawImageThumbnail(item.wav, imgMime, canvas, null).then(ok => {
               if (!ok) drawImageIcon(canvas);
               drawTypeBadge(canvas, 'IMAGE');
            });
            canvas.addEventListener('click', () => {
               const blob = new Blob([item.wav], { type: imgMime });
               const url = URL.createObjectURL(blob);
               if (typeof window !== 'undefined' && window.openImageViewer) {
                  window.openImageViewer({ url, name: item.name, downloadable: true, downloadUrl: url, downloadName: item.name });
               } else {
                  window.open(url, '_blank');
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
               }
            });
            row.appendChild(canvas);
         } else {
            // WAV waveform canvas
            const canvas = document.createElement('canvas');
            canvas.className = 'bucketitem-waveform';
            canvas.width = 160;
            canvas.height = 58;
            canvas.style.cursor = 'pointer';
            canvas.setAttribute('role', 'img');
            canvas.setAttribute('aria-label', `Waveform for ${item.name}, click to play`);
            drawWaveformFromWav(canvas, item.wav, item.pcm, item.sampleRate, item.channels, item.bitDepth);
            drawTypeBadge(canvas, 'WAV');
            canvas.addEventListener('click', async () => {
               // Set playing flag FIRST
               this.isPlaying = true;
               
               // Stop any existing playback first (WITHOUT component to force stop everything)
               stopAllAudioPlayback();
               
               // Add playing class
               canvas.classList.add('playing');
               
               // If PCM data is available, play it directly
               if (item.pcm && item.pcm.length && item.sampleRate) {
                  const scale = item.bitDepth === 24 ? 8388608 : (item.bitDepth === 32 ? 2147483648 : 32768);
                  const ctx = getGlobalAudioContext();
                  
                  const audioBuffer = ctx.createBuffer(item.channels || 1, item.pcm.length, item.sampleRate);
                  
                  if (item.channels === 1) {
                     const channelData = audioBuffer.getChannelData(0);
                     for (let i = 0; i < item.pcm.length; i++) {
                        channelData[i] = item.pcm[i] / scale;
                     }
                  } else if (item.channels === 2) {
                     const left = audioBuffer.getChannelData(0);
                     const right = audioBuffer.getChannelData(1);
                     for (let i = 0; i < item.pcm.length / 2; i++) {
                        left[i] = item.pcm[i * 2] / scale;
                        right[i] = item.pcm[i * 2 + 1] / scale;
                     }
                  }
                  
                  playAudioBuffer(audioBuffer, ctx, 1, canvas, () => {
                     this.isPlaying = false;
                  }, 'bucket');
               } else {
                  // Dispatch custom event for WAV playback (apps like xtrc may handle it)
                  const event = new CustomEvent('bucket-wav-play', {
                     detail: { name: item.name, data: item.wav, pcm: item.pcm, sampleRate: item.sampleRate, channels: item.channels, bitDepth: item.bitDepth, canvas }
                  });
                  window.dispatchEvent(event);

                  // If no app-specific handler played the audio, decode WAV and play directly.
                  // We check after a tick whether playback started (isPlaying still true means
                  // an external handler took over).
                  setTimeout(async () => {
                     if (window.__bucketWavPlayHandled) {
                        window.__bucketWavPlayHandled = false;
                        this.isPlaying = false;
                        return;
                     }
                     try {
                        const ctx = getGlobalAudioContext();
                        const audioBuffer = await ctx.decodeAudioData(item.wav.slice(0));
                        playAudioBuffer(audioBuffer, ctx, 1, canvas, () => {
                           this.isPlaying = false;
                        }, 'bucket');
                     } catch (err) {
                        this.isPlaying = false;
                    }
                  }, 50);
               }
            });
            row.appendChild(canvas);
         }
         } // end grid view mode else-block

         // Create edit button (only for WAV files — not for MIDI, module, or archive types)
         let editBtn = null;
         if (item.type !== 'midi' && item.type !== 'module' && item.type !== 'archive' && item.type !== 'raw' && item.type !== 'video') {
            editBtn = document.createElement('button');
            editBtn.className = 'bucketitem-edit';
            editBtn.textContent = 'EDIT';
            editBtn.setAttribute('aria-label', `Edit ${item.name}`);
            editBtn.addEventListener('click', async () => {
               // Image items: open in pixeledit
               if (item.type === 'image' && item.wav) {
                  const ext = (item.name.split('.').pop() || '').toLowerCase();
                  const imgMime = {
                     png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
                     gif: 'image/gif', bmp: 'image/bmp', webp: 'image/webp',
                     tiff: 'image/tiff', tga: 'image/x-tga', pcx: 'image/x-pcx',
                  }[ext] || 'image/png';
                  const blob = new Blob([item.wav], { type: imgMime });
                  const file = new File([blob], item.name, { type: imgMime });

                  // If we're already in pixeledit (window.open_from_file available), open in-place
                  if (typeof window.open_from_file === 'function') {
                     window.open_from_file(file);
                  } else {
                     // Open pixeledit in a new window with the image
                     const url = URL.createObjectURL(blob);
                     window.open(`/apps/pixeledit?url=${encodeURIComponent(url)}&name=${encodeURIComponent(item.name)}`, '_blank');
                     setTimeout(() => URL.revokeObjectURL(url), 60000);
                  }
                  return;
               }
               // Try to open in embedded wavedit (Electron/xtrc standalone)
               if (window.wavedit && typeof window.wavedit.loadSample === 'function') {
                  const fileName = item.name.endsWith('.wav') ? item.name : `${item.name}.wav`;
                  window.wavedit.loadSample(fileName, item.wav)
                     .catch(() => {});
               } else {
                  // Fallback for web version: use legacy method
                  try {
                     // Try to use bucketdb if available (azathoth legacyapps)
                     if (typeof require === 'function') {
                        try {
                           const bucketdb = require('../../../azathoth/legacyapps/src/common/bucketdb.js');
                           const blob = new Blob([item.wav], { type: 'audio/wav' });
                           await bucketdb.addSampleWithId(item.id, blob, { name: item.name });
                           window.open(`/apps/wavedit?id=${encodeURIComponent(item.id)}`, '_blank');
                        } catch (e) {
                           // bucketdb not available, try direct blob URL
                           const blob = new Blob([item.wav], { type: 'audio/wav' });
                           const url = URL.createObjectURL(blob);
                           window.open(`/apps/wavedit?url=${encodeURIComponent(url)}&name=${encodeURIComponent(item.name)}`, '_blank');
                        }
                     } else {
                        // No require available, use blob URL
                        const blob = new Blob([item.wav], { type: 'audio/wav' });
                        const url = URL.createObjectURL(blob);
                        window.open(`/apps/wavedit?url=${encodeURIComponent(url)}&name=${encodeURIComponent(item.name)}`, '_blank');
                     }
                  } catch (err) {
                  }
               }
            });
         }
         
         // Create remove button
         const removeBtn = document.createElement('button');
         removeBtn.className = 'removefrombucket icons2';
         removeBtn.textContent = '\uE82F';
         removeBtn.setAttribute('aria-label', `Remove ${item.name} from bucket`);
         removeBtn.addEventListener('click', async () => {
            // Encoding items: cancel encoding + remove placeholder (no IDB entry)
            if (item.encoding) {
               item._cancelled = true;
               this.items = this.items.filter((it) => it.id !== item.id);
               this.render();
               this.setStatus(`cancelled encoding: ${item.name}`);
               return;
            }
            // Confirm deletion if a confirmFn is set
            if (this.confirmFn) {
               const ok = await this.confirmFn(`Remove "${item.name}" from bucket?`);
               if (!ok) return;
            }
            // Stop playback if this item is currently playing
            if (item.type === 'midi') {
               // Stop MIDI playback
               const stopEvent = new CustomEvent('bucket-midi-stop', {
                  detail: { name: item.name }
               });
               window.dispatchEvent(stopEvent);
            } else {
               // Stop WAV playback
               const stopEvent = new CustomEvent('bucket-wav-stop');
               window.dispatchEvent(stopEvent);
            }
            this.remove(item.id);
         });
         
         row.appendChild(label);
         if (editBtn) row.appendChild(editBtn);
         row.appendChild(removeBtn);
         
         // Encoding items are not draggable (no data yet)
         if (item.encoding) {
            row.draggable = false;
            row.classList.add('encoding');
            this.itemsDiv.appendChild(row);
            continue;
         }
         row.draggable = true;
         row.addEventListener('dragstart', (e) => {
            e.dataTransfer.effectAllowed = 'copy';
            e.dataTransfer.setData('application/x-bucket-item-id', item.id);
            e.dataTransfer.setData('text/plain', item.id);
            // For module items, also set module URL data so adapters can accept drops
            if (item.type === 'module') {
               const blobUrl = URL.createObjectURL(new Blob([item.wav], { type: 'application/octet-stream' }));
               e.dataTransfer.setData('application/x-module-url', blobUrl);
               e.dataTransfer.setData('application/x-module-sample-name', item.name);
               // Revoke after drag completes
               setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
            }
            // For MIDI items, also set MIDI URL data
            if (item.type === 'midi') {
               const blobUrl = URL.createObjectURL(new Blob([item.wav], { type: 'audio/midi' }));
               e.dataTransfer.setData('application/x-midi-url', blobUrl);
               e.dataTransfer.setData('application/x-midi-sample-name', item.name);
               setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
            }
            // For video items, set video URL data
            if (item.type === 'video') {
               const blobUrl = URL.createObjectURL(new Blob([item.wav], { type: item.name.endsWith('.webm') ? 'video/webm' : 'video/mp4' }));
               e.dataTransfer.setData('application/x-video-url', blobUrl);
               e.dataTransfer.setData('application/x-video-name', item.name);
               setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
            }
            // For image items, set image URL data
            if (item.type === 'image') {
               const ext = (item.name.split('.').pop() || '').toLowerCase();
               const imgMime = {
                  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
                  gif: 'image/gif', bmp: 'image/bmp', webp: 'image/webp',
                  tiff: 'image/tiff', tga: 'image/x-tga', pcx: 'image/x-pcx',
               }[ext] || 'application/octet-stream';
               const blobUrl = URL.createObjectURL(new Blob([item.wav], { type: imgMime }));
               e.dataTransfer.setData('application/x-image-url', blobUrl);
               e.dataTransfer.setData('application/x-image-name', item.name);
               setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
            }
         });

         // Context menu (right-click) for download / remove
         row.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            _showBucketContextMenu(e.clientX, e.clientY, item, this);
         });

         this.itemsDiv.appendChild(row);
      }
      if (this.countEl) {
         const n = this.items.length;
         this.countEl.textContent = n > 0 ? n : '';
         this.countEl.style.display = n > 0 ? '' : 'none';
      }
      // Notify listeners (e.g. export modal accessible picker) that the
      // bucket contents changed so they can refresh their views.
      if (typeof window !== 'undefined') {
         window.dispatchEvent(new CustomEvent('bucket-changed', { detail: { count: this.items.length } }));
      }
   }

   // Lightweight progress update for a single encoding card — avoids full re-render.
   // Uses the canvas reference stored on the item by render().
   _updateEncodingCard(item, progress) {
      if (!item || !item._canvas) {
         // Canvas ref lost (e.g. after a render) — try to find it in the DOM
         if (this.itemsDiv && item) {
            const row = this.itemsDiv.querySelector(`[data-id="${item.id}"]`);
            if (row) {
               const canvas = row.querySelector('canvas.bucketitem-waveform');
               if (canvas) { item._canvas = canvas; }
            }
         }
      }
      if (item && item._canvas) {
         drawEncodingProgress(item._canvas, progress, 'ENCODING');
      }
   }

   // Transform an encoding card into a finished video card in-place — no full re-render.
   // Replaces the encoding canvas with a fresh video canvas inside the same row,
   // adds click-to-play handler, makes it draggable, removes the encoding class.
   _finalizeEncodingCard(item) {
      if (!this.itemsDiv || !item) return;
      const row = this.itemsDiv.querySelector(`[data-id="${item.id}"]`);
      if (!row) {
         // Row not found (e.g. after a re-render) — fall back to full render
         this.render();
         return;
      }

      // Remove encoding visual state
      row.classList.remove('encoding');
      row.draggable = true;

      // Find and replace the old encoding canvas with a fresh one.
      // A new canvas avoids any stale 2D context state from the progress drawing.
      const oldCanvas = item._canvas || row.querySelector('canvas.bucketitem-waveform');
      if (!oldCanvas) {
         this.render();
         return;
      }

      const mimeType = item.name.endsWith('.webm') ? 'video/webm' : 'video/mp4';
      const canvas = document.createElement('canvas');
      canvas.className = 'bucketitem-waveform';
      canvas.width = 160;
      canvas.height = 58;
      canvas.style.cursor = 'pointer';

      // Draw first frame (async), fall back to icon
      drawFirstFrame(item.wav, mimeType, canvas, null).then(ok => {
         if (!ok) drawVideoIcon(canvas);
         drawTypeBadge(canvas, 'VIDEO');
      }).catch(() => {
         drawVideoIcon(canvas);
         drawTypeBadge(canvas, 'VIDEO');
      });

      // Click-to-play handler
      canvas.addEventListener('click', () => {
         const blob = new Blob([item.wav], { type: mimeType });
         openVideoPlayer({ blob, name: item.name, downloadable: true, downloadName: item.name });
      });

      // Replace old canvas in the DOM
      if (oldCanvas.parentNode) {
         oldCanvas.parentNode.replaceChild(canvas, oldCanvas);
      } else {
         row.appendChild(canvas);
      }

      // Drag handler — set video URL data
      row.addEventListener('dragstart', (e) => {
         e.dataTransfer.effectAllowed = 'copy';
         e.dataTransfer.setData('application/x-bucket-item-id', item.id);
         e.dataTransfer.setData('text/plain', item.id);
         const blobUrl = URL.createObjectURL(new Blob([item.wav], { type: mimeType }));
         e.dataTransfer.setData('application/x-video-url', blobUrl);
         e.dataTransfer.setData('application/x-video-name', item.name);
         setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      });

      // Context menu
      row.addEventListener('contextmenu', (e) => {
         e.preventDefault();
         _showBucketContextMenu(e.clientX, e.clientY, item, this);
      });

      // Clean up internal fields
      delete item._canvas;
   }

   async downloadZip({ prefix = '', normalize = true } = {}) {
      if (!this.items.length) { this.setStatus('bucket empty'); return; }
      try {
         this.setStatus('packing zip…');
         const items = this.items.slice();
         const sourceLabel = SOURCE_LABEL;
         async function* gen() {
            for (const it of items) {
               const fileType = it.type || 'wav';
               let ext = 'wav';
               let mimeType = 'audio/wav';
               
               if (fileType === 'midi') {
                  ext = 'mid';
                  mimeType = 'audio/midi';
               } else if (fileType === 'module') {
                  ext = 'mod';
                  mimeType = 'application/octet-stream';
               } else if (fileType === 'video') {
                  // Keep original extension or default to webm
                  const origExt = (it.name.split('.').pop() || '').toLowerCase();
                  ext = ['mp4', 'webm', 'ogv', 'mov', 'mkv', 'avi', 'flv', 'wmv'].includes(origExt) ? origExt : 'webm';
                  mimeType = ext === 'webm' ? 'video/webm' : 'video/mp4';
               } else if (fileType === 'image') {
                  // Keep original extension or default to png
                  const origExt = (it.name.split('.').pop() || '').toLowerCase();
                  ext = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'tiff', 'tif', 'tga', 'pcx', 'psd', 'ico', 'lbm', 'ilbm', 'svg'].includes(origExt) ? origExt : 'png';
                  mimeType = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
               } else if (fileType === 'archive' || fileType === 'raw') {
                  ext = 'bin';
                  mimeType = 'application/octet-stream';
               }
               // RBS files should be converted to MIDI before being added to bucket, so no RBS export
               
               // Strip any existing extension first, then add the correct one
               const raw  = (it.name || 'sample').replace(/\.[^.]+$/, '');
               const safe = normalize ? raw.replace(/[^a-zA-Z0-9_-]/g, '_') : raw;
               const subdir = prefix || (sourceLabel[it.source] || it.source || 'samples').toLowerCase();
               yield {
                  name: `${subdir}/${safe}.${ext}`,
                  lastModified: new Date(it.addedAt || Date.now()),
                  input: new Blob([it.wav], { type: mimeType })
               };
            }
         }
         const blob = await downloadZip(gen()).blob();
         const a = document.createElement('a');
         a.href = URL.createObjectURL(blob);
         a.download = `${prefix || 'xtrc_bucket'}_${this.kitIdx++}.zip`;
         a.click();
         setTimeout(() => URL.revokeObjectURL(a.href), 200);
         this.setStatus(`downloaded ${this.items.length} sample(s)`);
      } catch (err) {
         console.error('[bucket] zip failed', err);
         this.setStatus('zip failed');
      }
   }
}
